import Model from "@/lib/model/model";
import {
  ExamComponent,
  SectionPageType
} from "@/lib/model/exambuilder/exam-component/exam";
import {
  MustComponent,
  NonessentialComponent
} from "@/lib/model/exambuilder/exam-component/common";

class StepItem extends Model {
  name = "";
  frequency = 0;
}

class Rule extends Model {
  duration = 0;
  times = 0;
  mergeType = "";
}
class RuleConfigItem extends Model {
  rule = new Rule();
  operation = 0;
  message = "";
  duration = 0;
  showType = 0;
}

export class VideoConfig extends Model {
  successVersion = 2;

  public objectArrayClasses() {
    return {
      stepList: StepItem,
      ruleConfigList: RuleConfigItem
    };
  }
}

export class QuestionnaireConfigData extends Model {
  questionnaireId = 0;
}

export class QuestionnaireConfig extends Model {
  use = 0;
  configData = new QuestionnaireConfigData();
}

export class ChannelConfig extends Model {
  channelType = "aliyun";
  uploadType = "cos";
}

export class VideoDetection extends Model {
  use = 0;
  configData = new VideoConfig();
}

export class ReplacementDetection extends NonessentialComponent {
  configData = {};
}

export class FaceConfig extends Model {
  scar = true;
}

export class FaceDetection extends Model {
  use = 0;
  configData = new FaceConfig();
}

export class FaceAssessment extends Model {
  use = 0;
}

export class Game extends NonessentialComponent {
  componentSort = 0;
  gameTypeList = [0];
}

export class WatermarkConfig extends NonessentialComponent {
  use = 1;
  configData = {
    contentType: 2
  };
}

export class WarningConfig extends Model {
  icon = "";
  title = "";
  content = "";
  buttonText = "我知道了";
  violationNumber = 0;
}

export class ScreenshotConfig extends Model {
  protect = true;
  whiteList = ["descAfter", "testExam"];
  violationNumber = 0;
  recordWarning = new WarningConfig().assign({
    title: "面试过程中严禁录屏",
    content:
      "请勿继续违规操作，否则将取消您的考试成绩。请您取消录屏后，再返回面试。",
    buttonText: "返回面试"
  });
  screenshotWarning = {
    mild: new WarningConfig().assign({
      title: "面试过程中严禁截屏",
      content: "请勿继续违规操作，否则将取消您的考试成绩"
    }),
    moderate: new WarningConfig().assign({
      title: "再次截屏，成绩就作废",
      content: "您已经在面试过程中多次截屏，再次截屏，本次面试的考试成绩将作废"
    }),
    severe: new WarningConfig().assign({
      title: "面试作废",
      content:
        "由于您在面试过程中多次截屏，本次面试作废，如有疑问请联系企业招聘人员"
    })
  };

  public objectArrayClasses() {
    return {
      whiteList: ""
    };
  }
}

export class ScreenshotDetection extends NonessentialComponent {
  configData = new ScreenshotConfig();
}

export class JumpOutConfig extends Model {
  violationNumber = 0;
  warning = {
    mild: new WarningConfig().assign({
      title: "面试过程中严禁切屏",
      content: "请勿继续违规操作，否则将取消您的考试成绩"
    }),
    moderate: new WarningConfig().assign({
      title: "再次切屏，成绩就作废",
      content: "您已经在面试过程中多次切屏，再次切屏，本次面试的考试成绩将作废"
    }),
    severe: new WarningConfig().assign({
      title: "面试作废",
      content:
        "由于您在面试过程中多次切屏，本次面试作废，如有疑问请联系企业招聘人员"
    })
  };
}

export class JumpOutDetection extends NonessentialComponent {
  configData = new JumpOutConfig();
}

export class ColorDetection extends NonessentialComponent {
  componentSort = 0;
  configData = {
    sectionPageConfig: ExamComponent.getDefaultSectionPage(
      SectionPageType.Color
    ),
    questionTimeConfig: {
      questionTimeText: ["[限时]", "后将自动提交"],
      questionTimeLv1: 10,
      questionTimeLv1Switch: true,
      questionTimeLv2: 15,
      questionTimeLv2Switch: true
    },
    questionNextBtnText: "下一步"
  };
}

export class HandForm extends Model {
  text = "";
  videoUrl = "";
  coverUrl = "";
  virMakerVideoId = 0;

  constructor(text: string) {
    super();
    this.text = text;
  }
}

// 获取不同语言的手臂完整提示文案
export function getArmCompleteConfigMessageList(lang: number): HandForm[] {
  switch (lang) {
    case 0: // 中文
    case 8: // 中文(方言)
    case 9: // 中文(粤语)
      return [
        new HandForm("请卷起袖子，露出全部左手手臂，在屏幕内完整展示"),
        new HandForm("请卷起袖子，露出全部右手手臂，在屏幕内完整展示")
      ];
    case 1: // 英语
      return [
        new HandForm(
          "Please roll up your sleeve and fully expose your left arm for complete display on screen"
        ),
        new HandForm(
          "Please roll up your sleeve and fully expose your right arm for complete display on screen"
        )
      ];
    case 2: // 马来语
      return [
        new HandForm(
          "Sila gulung lengan baju anda dan dedahkan sepenuhnya lengan kiri anda untuk paparan lengkap pada skrin"
        ),
        new HandForm(
          "Sila gulung lengan baju anda dan dedahkan sepenuhnya lengan kanan anda untuk paparan lengkap pada skrin"
        )
      ];
    case 3: // 日语
      return [
        new HandForm(
          "袖をまくり上げ、左腕全体を画面内に完全に表示してください"
        ),
        new HandForm("袖をまくり上げ、右腕全体を画面内に完全に表示してください")
      ];
    case 4: // 越南语
      return [
        new HandForm(
          "Vui lòng xắn tay áo và để lộ hoàn toàn cánh tay trái của bạn để hiển thị đầy đủ trên màn hình"
        ),
        new HandForm(
          "Vui lòng xắn tay áo và để lộ hoàn toàn cánh tay phải của bạn để hiển thị đầy đủ trên màn hình"
        )
      ];
    case 5: // 韩语
      return [
        new HandForm(
          "소매를 걷어 올리고 왼쪽 팔 전체를 화면에 완전히, 보여주세요"
        ),
        new HandForm(
          "소매를 걷어 올리고 오른쪽 팔 전체를 화면에 완전히 보여주세요"
        )
      ];
    case 6: // 法语
      return [
        new HandForm(
          "Veuillez remonter votre manche et exposer entièrement votre bras gauche pour un affichage complet à l'écran"
        ),
        new HandForm(
          "Veuillez remonter votre manche et exposer entièrement votre bras droit pour un affichage complet à l'écran"
        )
      ];
    case 7: // 印尼语
      return [
        new HandForm(
          "Harap gulung lengan baju Anda dan tunjukkan seluruh lengan kiri Anda untuk tampilan lengkap di layar"
        ),
        new HandForm(
          "Harap gulung lengan baju Anda dan tunjukkan seluruh lengan kanan Anda untuk tampilan lengkap di layar"
        )
      ];
    default:
      return [
        new HandForm("请卷起袖子，露出全部左手手臂，在屏幕内完整展示"),
        new HandForm("请卷起袖子，露出全部右手手臂，在屏幕内完整展示")
      ];
  }
}

export class HandDetection extends NonessentialComponent {
  componentSort = 0;
  configData = {
    sectionPageConfig: ExamComponent.getDefaultSectionPage(
      SectionPageType.Finger
    ),
    noticeMessageList: [
      new HandForm("请将左手手背对准虚线框"),
      new HandForm("请将左手手心对准虚线框"),
      new HandForm("请在虚线框内做三次握拳动作"),
      new HandForm("请将右手手背对准虚线框"),
      new HandForm("请将右手手心对准虚线框"),
      new HandForm("请在虚线框内做三次握拳动作")
    ],
    // !临时方案：把下面两句话按照 lang 来翻译
    noArmCompleteConfigMessageList: getArmCompleteConfigMessageList(0),
    fingerCheckTestComplete: false,
    fingerCheckTestFlexible: false,
    fingerCheckTestScar: false,
    armCheckTestHide: false,
    armCheckTestComplete: false,
    faceCheckTestShow: false,
    processVersion: 0,
    videoUploadType: 0,
    noArmCompleteConfig: {
      operate: 0,
      numberLimit: 0
    },
    noRollUpSleevesConfig: {
      operate: 0,
      numberLimit: 0
    },
    wsaCodeUrl: ""
  };
}

export class BodyDetection extends NonessentialComponent {
  componentSort = 0;
  configData = {
    sectionPageConfig: ExamComponent.getDefaultSectionPage(
      SectionPageType.Body
    ),
    noticeMessageList: [
      new HandForm(
        "请先站直身体，双脚并拢。保持背部挺直，并将双臂向两侧张开至与肩同宽的位置"
      ),
      new HandForm("请保持站立，并张开双臂至与肩部平行，手掌朝下。双腿并拢"),
      new HandForm(
        "身体向右侧旋转蹲下，下蹲后确保您的膝盖和脚尖保持在同一方向"
      ),
      new HandForm(
        "保持右侧腿部单脚站立于地面，左侧腿部抬起，左腿抬起时，右臂向前，左臂向后"
      ),
      new HandForm(
        "保持左侧腿部单脚站立于地面，右侧腿部抬起，右腿抬起时，左臂向前，右臂向后"
      )
    ]
  };
}

export class CertPhotoCaptureConfig extends Model {
  checkFaceComplete = true; // 是否检测脸部完整性
  checkFaceAngle = false; // 是否检测脸部角度
  checkLighting = false; // 是否检测光线
}

export class CertPhotoCapture extends NonessentialComponent {
  use = 0;
  componentSort = 0;
  configData = new CertPhotoCaptureConfig();
}

// 手写签名
export class SignatureConfig extends NonessentialComponent {
  use = 0;
  componentSort = 0;
  configData = new SignatureConfigData();
}

export class SignatureConfigData extends Model {
  title = ""; // 标题名称
  desc = ""; // 描述信息
  buttonText = "同意并签名"; // 按钮名称
  noticeMessage = "请在规定区域内，字迹清晰地写出你的姓名"; // 提示信息
  checkContent = false;
}

export class LivenessRecognitionConfig extends Model {
  lrType = 0;
  lrErrorAutoSubmit = 0;
  lrRetryNumberLimit = 0;
}

export class LivenessRecognition extends NonessentialComponent {
  componentSort = 0;
  configData = new LivenessRecognitionConfig();
}

export class NameIdCheckConfig extends Model {
  maxSubmitNumber = 2;
  noticeMessage = "姓名或证件号与实际证件不匹配，请重新输入";
}

export class NameIdCheck extends NonessentialComponent {
  componentSort = 0;
  configData = new NameIdCheckConfig();
}

export class VrGuideConfigData extends Model {
  vrUrl = "";
}

export class VrGuideConfig extends NonessentialComponent {
  configData = new VrGuideConfigData();
}

export class LocationConfig extends Model {
  longitude = 116.397464;
  latitude = 39.908803;
  address = "";
  detailAddress = "";
}
export class WorkLocationConfig extends Model {
  locationList: LocationConfig[] = [];

  public objectArrayClasses() {
    return {
      locationList: LocationConfig
    };
  }
}

export class WorkLocation extends NonessentialComponent {
  configData = new WorkLocationConfig();
}

export class ResumeParseConfig extends Model {
  noticeMessage =
    "请上传一份最新的简历，我们会结合你的过往工作情况来进行面试。";
}

export class ResumeParse extends MustComponent {
  use = 2;
  configData = new ResumeParseConfig();
}

export class BgCheckConfig extends Model {
  bgCheckProductId = 0;
}

export class BgCheck extends NonessentialComponent {
  componentSort = 0;
  configData = new BgCheckConfig();
}
const CHECKED = 1; // 没有选上
export class LinkListForIntelligentQA extends Model {
  qaId = 0;
  isRecommended = 0; // 是否推荐
}

export class TableDataItem extends Model {
  id = 0;
  qaId = 0;
  questionNo = "";
  shortName = "";
  standardAnswer = "";
  status = 1;
  standardQuestion = "";
  isRecommended = 1;
  isRecommendedBool = false;
}
export class IntelligentQAConfig extends Model {
  isBeforeExam = CHECKED; // 最后字段要为1或者2
  isAfterExam = CHECKED;
  greetingFirstTime =
    "您好，欢迎来参加面试，我是您的专属AI答疑官小娜，关于面试您有什么问题都可以向小娜提问。"; //首次欢迎语
  greetingAgain = "嗨，又见面了，您还有什么问题想要了解吗？"; //非首次欢迎语
  responseSeveralMatches = "您想问的是否是以下问题？"; //命中多个问题话术
  responseNoMatch = "很抱歉，小娜没有您这个问题的答案。"; //未命中多个问题话术
  link: LinkListForIntelligentQA[] = []; // 问题库list
  linkDetail: TableDataItem[] = [];

  public objectArrayClasses() {
    return {
      link: LinkListForIntelligentQA,
      linkDetail: TableDataItem
    };
  }
}
export class IntelligentQA extends Model {
  use = 0;
  configData = new IntelligentQAConfig();
}

export class DeviceDetectionConfig extends Model {
  deviceDetection = true; // 摄像头检测
  maikefeng = true; // 麦克风检测
  yangshengqi = true; // 扬声器检测
  noiseDetection = true; // 噪声检测
  network = true; // 全局弱网检测
  version = true; // 微信版本检测
}
export class DeviceDetection extends NonessentialComponent {
  componentSort = 0;
  configData: DeviceDetectionConfig = new DeviceDetectionConfig();
  use = 1;
}

export class DescBefore extends Model {
  descBefore =
    "你好，我是本次面试的面试官，欢迎参加本公司的线上面试！面试预计需要10分钟，请你提前安排在网络良好、光线亮度合适、且相对安静的环境参加这次面试，以免影响本次面试的结果。如果你在面试过程中遇到问题，请与我们的招聘人员联系。";
  descBeforeShowWay = 0;
  descBeforeShowWayImgUrl = "";
  descBeforeVideoMustPlay = 0;
}

export class DescBeforeConfig extends MustComponent {
  configData = new DescBefore();
  use = 1;
}

export class DescAfter extends Model {
  descAfter =
    "感谢你参加面试，面试结果我们会在1至3个工作日内通知你。我们期待你的加入，再见！";
  descAfterShowWay = 0;
  descAfterShowWayImgUrl = "";
  descAfterShowWayIsSame = 0;
}

export class DescAfterConfig extends MustComponent {
  configData = new DescAfter();
  use = 1;
}

export class Privacy extends Model {
  privacyHtmlUrl = "";
}

export class PrivacyConfig extends MustComponent {
  configData = new Privacy();
  use = 0;
}

export class Publicity extends Model {
  publicityPageContent =
    "海纳隶属于北京群星闪耀科技有限公司，致力于以AI技术赋能人力资源行业，帮助千千万万人才和企业做好人岗匹配、提升组织效率。拥有雄厚资本支持，可保证团队投入大量资金和人力到产品研发上，持续提升AI及产品能力，为大型集团提供可靠、持久的杰出服务。";
  publicityPageVideoUrl = "";
  publicityPageVideoCoverUrl =
    "https://oss.hina.com/app/hina/2021/07/10/3f1396a2ddd6a8493c582f22c69dae39.png";
  publicityPageVideoUseWhich = 0; // 宣传视频使用类型(0:不使用:1默认视频:2:自定义视频)
  publicityPageVideoUse = 0;
  publicityPageVideoCoverUse = 0;
  publicityPageVideoAutoplay = 1;

  // 是否为默认封面
  get publicityPageVideoCoverUseWhich() {
    return this.publicityPageVideoCoverUrl ===
      "https://oss.hina.com/app/hina/2021/07/10/3f1396a2ddd6a8493c582f22c69dae39.png"
      ? 1
      : 2;
  }
}

export class PublicityConfig extends MustComponent {
  configData = new Publicity();
  use = 0;
}

export class TestExam extends NonessentialComponent {
  configData = {
    testExamId: 0
  };
}
